import { SHTableBody } from "@components/design-systems";
import { FeatureAnalysisRowDTO } from "@models/platform-analysis/entities/steps/feature";
import { Column, Row } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { memo, useCallback, useMemo } from "react";
import { getTableVirtualizationConfigs } from "../../../utils";
import { AnalysisTableRow } from "../row";

type AnalysisTableBodyProps = {
  rows: Row<FeatureAnalysisRowDTO>[];
  columns: Column<FeatureAnalysisRowDTO>[];
  containerRefElement: HTMLDivElement | null;
};

export const AnalysisTableBody = memo(
  ({ rows, columns, containerRefElement }: AnalysisTableBodyProps) => {
    const {
      rowOverscan,
      columnOverscan,
      measureElementHeight,
      measureElementWidth,
      rowEstimateSizeFunction,
      columnEstimateSizeFunction,
    } = useMemo(() => getTableVirtualizationConfigs(columns), [columns]);

    const rowVirtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => containerRefElement,
      estimateSize: rowEstimateSizeFunction,
      measureElement: measureElementHeight,
      overscan: rowOverscan,
    });

    const getColumnWidthCallBack = useCallback(
      (index: number) => columnEstimateSizeFunction(index),
      [columnEstimateSizeFunction],
    );
    const columnVirtualizer = useVirtualizer({
      count: columns.length,
      getScrollElement: () => containerRefElement,
      estimateSize: getColumnWidthCallBack,
      horizontal: true,
      measureElement: measureElementWidth,
      overscan: columnOverscan,
    });

    const rowVirtualizerItems = rowVirtualizer.getVirtualItems();
    const rowVirtualizerTotalSize = rowVirtualizer.getTotalSize();
    const columnVirtualizerItems = columnVirtualizer.getVirtualItems();

    return (
      <SHTableBody
        sx={{
          display: "grid",
          height: `${rowVirtualizerTotalSize}px`,
          position: "relative",
          borderBottom: "none",
        }}
      >
        {rowVirtualizerItems.map((virtualRow) => {
          const row: Row<FeatureAnalysisRowDTO> = rows[virtualRow.index];
          const isRowExpanded = row.getIsExpanded();

          return (
            <AnalysisTableRow
              key={row.id}
              row={row}
              virtualRow={virtualRow}
              rowVirtualizer={rowVirtualizer}
              columns={columns}
              columnVirtualizerItems={columnVirtualizerItems}
              isRowExpanded={isRowExpanded}
            />
          );
        })}
      </SHTableBody>
    );
  },
);
